/**
 * Utility functions for handling module ranking positions in nested components.
 * 
 * Module rankings are used for SEO and analytics to track the position of content
 * modules on a page. When components are nested within each other, we need to ensure
 * that child components don't duplicate their parent's module ranking.
 */

/**
 * Determines if a component should have a module ranking position.
 * 
 * @param componentRanking - The module ranking position assigned to the current component
 * @param parentRanking - The module ranking position of the parent component (if nested)
 * @returns The module ranking to use, or undefined if the component is nested and should not have a ranking
 * 
 * @example
 * // Component with no parent - returns its own ranking
 * resolveModuleRanking("3", undefined) // Returns "3"
 * 
 * @example
 * // Component nested within another ranked component - returns undefined
 * resolveModuleRanking("5", "3") // Returns undefined
 * 
 * @example
 * // Component with no ranking assigned - returns undefined
 * resolveModuleRanking(undefined, undefined) // Returns undefined
 */
export function resolveModuleRanking(
  componentRanking?: string,
  parentRanking?: string
): string | undefined {
  // If this component is embedded within another component that has a ranking,
  // ignore the child's ranking to prevent duplicate rankings
  if (parentRanking !== undefined) {
    return undefined;
  }

  // Otherwise, use the component's own ranking (if it has one)
  return componentRanking;
}

/**
 * Checks if a component is nested within a parent component that has module ranking.
 * 
 * @param parentRanking - The module ranking position of the parent component
 * @returns true if the component is nested within a ranked parent, false otherwise
 * 
 * @example
 * isNestedInRankedParent("3") // Returns true
 * isNestedInRankedParent(undefined) // Returns false
 */
export function isNestedInRankedParent(parentRanking?: string): boolean {
  return parentRanking !== undefined;
}

/**
 * Gets the appropriate module ranking for a component based on its context.
 * This is a more semantic version of resolveModuleRanking that explicitly
 * handles the nested component use case.
 * 
 * @param options - Configuration object
 * @param options.ownRanking - The module ranking assigned to this component
 * @param options.parentRanking - The module ranking of the parent (if nested)
 * @param options.forceRanking - If true, returns the component's ranking even if nested
 * @returns The module ranking to use, or undefined if nested
 * 
 * @example
 * // Standard nested case - child ranking is suppressed
 * getModuleRanking({ ownRanking: "5", parentRanking: "3" }) // Returns undefined
 * 
 * @example
 * // Force ranking even when nested (use sparingly)
 * getModuleRanking({ 
 *   ownRanking: "5", 
 *   parentRanking: "3", 
 *   forceRanking: true 
 * }) // Returns "5"
 * 
 * @example
 * // Component not nested - returns its own ranking
 * getModuleRanking({ ownRanking: "5" }) // Returns "5"
 */
export function getModuleRanking(options: {
  ownRanking?: string;
  parentRanking?: string;
  forceRanking?: boolean;
}): string | undefined {
  const { ownRanking, parentRanking, forceRanking = false } = options;

  // If forceRanking is true, always return the component's own ranking
  if (forceRanking) {
    return ownRanking;
  }

  // Use the resolveModuleRanking logic
  return resolveModuleRanking(ownRanking, parentRanking);
}
